// useTranscriptionWatchdog — open-chat resilience for stalled transcriptions.
//
// Main-encounter recordings are sent async: api.transcript returns 200 the instant
// the backend acks, and the UI flips to "transcribing" while it waits for the SSE
// "transcriptionCompleted" event to paint the transcript and clear the spinner. If
// that event never arrives (a backend failure, or just a dropped SSE on a flaky
// link), the spinner spins forever and the note stays blank. Regenerations already
// have a recovery+abort ladder (useJobs.startRegenerating); transcription had none.
//
// This watchdog mirrors that idea, NON-DESTRUCTIVELY:
//   - poll getChat on a ladder; if the transcript actually landed (dropped SSE),
//     paint it and stop;
//   - at a hard ceiling, declare an honest failure (clear spinner + a toast with a
//     read-only Reload action) instead of hanging forever.
//
// It NEVER re-transcribes. The only client re-trigger — api.transcript(null, ...)
// against the durable store — was removed because it deletes the audio backup before
// a transcript is confirmed, i.e. it can CAUSE recording loss. Recovery here is
// detection + honest UX only; genuine server failures are fixed at the source.
//
// That removal used to be pinned by RecordingRecoveryBar.selfheal.test.js, which
// drove the recovery bar's poll and asserted api.transcript was never reached. The
// bar went headless on 2026-07-30 and lost the poll, so the test was deleted with it.
// Stated rather than quietly re-pointed: the re-trigger is gone structurally (there
// is no longer a poll that could call it), but it is no longer test-pinned.

import { useCallback, useEffect, useMemo, useRef } from "react";
import { chatTranscriptSignal } from "../utils/transcriptRefresh";

// Wall-clock from when the spinner appears. Conservative against false-fails on long
// segmented / drain-assembled dictations (the async request returns immediately, so
// the axios timeout never applies — this ladder is the only clock).
export const DEFAULT_POLL_MS = 90000;
export const DEFAULT_CEILING_MS = 300000;
// Per-arm ceiling for LARGE recordings (above the chunk threshold): server STT
// alone budgets 120s x 3 attempts = 360s, so the 300s default declared honest
// failure on long dictations the server went on to finish. 480s = STT worst
// case + upload/assembly margin.
export const LONG_RECORDING_CEILING_MS = 480000;

// Pure, React-free core so the ladder is unit-testable with a virtual clock. The
// hook below is a thin wrapper that supplies the real dependencies.
//
//   getChat(chatId)        -> Promise<{ data: chat }>   (read-only)
//   onLanded(chat, ctx)    -> paint + clear the spinner; ctx = { chatId, recordingId }
//   onStalled(ctx)         -> honest-fail UI; ctx = { chatId, recordingId, reload }
//   signalOf(chat)         -> transcript fingerprint (defaults to chatTranscriptSignal)
//   scheduler              -> { setTimeout, clearTimeout } (injectable for tests)
export function createTranscriptionWatchdog({
  getChat,
  onLanded,
  onStalled,
  signalOf = chatTranscriptSignal,
  // Wrap the globals — a bare `{ setTimeout, clearTimeout }` unbinds them from
  // `window`, so `scheduler.setTimeout(...)` throws "Illegal invocation" in real
  // browsers (jsdom does NOT enforce this, so unit tests with an injected scheduler
  // never caught it — found via stage smoke). arm() throwing here would otherwise
  // break parseRecording for EVERY recording, not just recovery.
  scheduler = {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (id) => clearTimeout(id),
  },
  pollMs = DEFAULT_POLL_MS,
  ceilingMs = DEFAULT_CEILING_MS,
} = {}) {
  // recordingId -> { chatId, startedAt, baseline, timers: [timerId] }
  const active = new Map();

  // Discrete poll ticks before the ceiling: pollMs, 2*pollMs, ... strictly < ceiling.
  function pollTimes(ceiling) {
    const ticks = [];
    for (let t = pollMs; t < ceiling; t += pollMs) ticks.push(t);
    return ticks;
  }

  // True only if THIS entry is still the live one (not disarmed, not superseded by a
  // re-record of the same recordingId with a newer startedAt). Mirrors the regen
  // ladder's startedAt guard.
  function live(recordingId, startedAt) {
    const e = active.get(recordingId);
    return e != null && e.startedAt === startedAt;
  }

  function clearTimers(entry) {
    if (entry && entry.timers) entry.timers.forEach((id) => scheduler.clearTimeout(id));
  }

  function disarm(recordingId) {
    const e = active.get(recordingId);
    if (!e) return;
    clearTimers(e);
    active.delete(recordingId);
  }

  function disarmAll() {
    active.forEach((e) => clearTimers(e));
    active.clear();
  }

  // Read once; return the chat if the transcript has advanced past baseline, else
  // null. Read failures are swallowed (treated as "not advanced") so a timer never
  // throws and a transient network blip never false-fails.
  async function readIfAdvanced(chatId, baseline) {
    let res;
    try {
      res = await getChat(chatId);
    } catch (e) {
      return null;
    }
    const chat = res && res.data;
    return signalOf(chat) !== baseline ? chat : null;
  }

  // One handler for every tick. A non-final (poll) tick that finds nothing simply
  // waits for the next; the final (ceiling) tick honest-fails instead. Keeping the
  // live-guard / read / re-check sequence in ONE place means the disarm-vs-supersede
  // race can't drift between two copies.
  async function tick(recordingId, startedAt, chatId, baseline, isFinal, dataIdentity) {
    if (!live(recordingId, startedAt)) return;
    const advanced = await readIfAdvanced(chatId, baseline);
    if (!live(recordingId, startedAt)) return; // disarmed/superseded during the await
    if (advanced) {
      onLanded(advanced, { chatId, recordingId, dataIdentity });
      disarm(recordingId);
      return;
    }
    if (!isFinal) return; // a poll tick just waits for the next one
    // Ceiling reached without the transcript landing: honest fail. Hand back a
    // read-only reload — one more getChat, paint if it has since landed. No
    // re-transcription, ever.
    const reload = async () => {
      const late = await readIfAdvanced(chatId, baseline);
      if (late) onLanded(late, { chatId, recordingId, dataIdentity });
    };
    onStalled({ chatId, recordingId, reload, dataIdentity });
    disarm(recordingId);
  }

  function arm({ chatId, recordingId, startedAt, baseline, ceilingMs: armCeilingMs, dataIdentity }) {
    // Re-arming the same recordingId supersedes the prior cycle.
    disarm(recordingId);
    // Per-arm ceiling override: long recordings need a bigger honest-fail
    // budget than the default (server STT alone can legitimately run 360s).
    const ceiling = armCeilingMs > 0 ? armCeilingMs : ceilingMs;
    const entry = { chatId, startedAt, baseline, timers: [] };
    active.set(recordingId, entry);
    pollTimes(ceiling).forEach((t) => {
      entry.timers.push(scheduler.setTimeout(() => tick(recordingId, startedAt, chatId, baseline, false, dataIdentity), t));
    });
    entry.timers.push(scheduler.setTimeout(() => tick(recordingId, startedAt, chatId, baseline, true, dataIdentity), ceiling));
  }

  return { arm, disarm, disarmAll };
}

// React wrapper. Builds the watchdog once with the caller's paint/notify callbacks
// and tears all timers down on unmount. Returns a stable { arm, disarm }.
//
//   onLanded(chat, ctx)  — paint the refetched chat into local state (clears the
//                          transcribing spinner).
//   onStalled(ctx)       — show the honest-fail toast with ctx.reload as its action.
export default function useTranscriptionWatchdog({ getChat, onLanded, onStalled } = {}) {
  const cbRef = useRef({ getChat, onLanded, onStalled });
  cbRef.current = { getChat, onLanded, onStalled };

  const wdRef = useRef(null);
  if (wdRef.current == null) {
    wdRef.current = createTranscriptionWatchdog({
      getChat: (id) => cbRef.current.getChat(id),
      onLanded: (chat, ctx) => cbRef.current.onLanded(chat, ctx),
      onStalled: (ctx) => cbRef.current.onStalled(ctx),
    });
  }

  useEffect(() => {
    const wd = wdRef.current;
    return () => wd.disarmAll();
  }, []);

  const arm = useCallback((opts) => wdRef.current.arm(opts), []);
  const disarm = useCallback((recordingId) => wdRef.current.disarm(recordingId), []);
  return useMemo(() => ({ arm, disarm }), [arm, disarm]);
}
